import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Users, Search, Clock, Mail, Shield } from 'lucide-react';
import axios from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const AllUsers = () => {
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredUsers = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.team_name && user.team_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const formatLastLogin = (lastLogin) => {
        if (!lastLogin) return 'Never';
        try {
            return formatDistanceToNow(new Date(lastLogin), { addSuffix: true });
        } catch {
            return 'Unknown';
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'admin':
            case 'ADMIN':
                return 'bg-red-600/20 text-red-400 border-red-600/50';
            case 'coach':
            case 'COACH':
                return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
            case 'player':
            case 'PLAYER':
                return 'bg-green-600/20 text-green-400 border-green-600/50';
            default:
                return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
        }
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="text-center text-gray-400 py-10">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-7xl">
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Users className="h-8 w-8 text-blue-500" />
                    <h1 className="text-3xl font-bold text-white">All Users</h1>
                </div>
                <p className="text-gray-400">Manage all users in the system</p>
            </div>

            {/* Search */}
            <div className="mb-6">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder="Search by username, email, role, or team..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 bg-[#1a1a1b] border-gray-700 text-white"
                    />
                </div>
            </div>

            {/* Users Table */}
            <Card className="bg-[#1a1a1b] border-gray-800">
                <CardHeader>
                    <CardTitle className="text-white flex items-center justify-between">
                        <span>Users ({filteredUsers.length})</span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-700">
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Username</th>
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Role</th>
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Team</th>
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Last Login</th>
                                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Joined</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="text-center py-8 text-gray-400">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <tr key={user.id} className="border-b border-gray-800 hover:bg-[#0a0a0b] transition-colors">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Link to={`/profile/${user.username}`}>
                                                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                                                            {user.username.substring(0, 2).toUpperCase()}
                                                        </div>
                                                    </Link>
                                                    <Link to={`/profile/${user.username}`} className="text-white font-medium hover:underline hover:text-blue-400">
                                                        {user.username}
                                                    </Link>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2 text-gray-300">
                                                    <Mail className="h-4 w-4 text-gray-500" />
                                                    {user.email}
                                                </div>
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role)}`}>
                                                    <Shield className="h-3 w-3" />
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                {user.team_name ? (
                                                    <span className="text-white">{user.team_name}</span>
                                                ) : (
                                                    <span className="text-gray-500 italic">No team</span>
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2 text-gray-300">
                                                    <Clock className="h-4 w-4 text-gray-500" />
                                                    <span className={user.last_login ? 'text-white' : 'text-gray-500 italic'}>
                                                        {formatLastLogin(user.last_login)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-gray-400">
                                                {new Date(user.created_at).toLocaleDateString()}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AllUsers;
